import React from 'react';
import { Zap, Clock, Database, Globe } from 'lucide-react';
import { AutomationRulesTab } from '../../components/automation';

interface SystemRule {
  name: string;
  trigger: string;
  type: 'firestore_trigger' | 'scheduled' | 'callable';
  description: string;
}

const systemRules: SystemRule[] = [
  { name: 'New Lead Notifications', trigger: 'onLeadCreated', type: 'firestore_trigger', description: 'Notifies relevant users when a new lead is created.' },
  { name: 'Lead Assignment Tasks', trigger: 'onLeadAssigned', type: 'firestore_trigger', description: 'Auto-generates follow-up tasks when a lead is assigned.' },
  { name: 'Appointment Confirmation Emails', trigger: 'onAppointmentCreated', type: 'firestore_trigger', description: 'Sends confirmation emails when new appointments are created.' },
  { name: 'Determination Change Handler', trigger: 'onLeadDeterminationChanged', type: 'firestore_trigger', description: 'Handles billing impact and notifications when a lead determination changes.' },
  { name: 'Survey Response Handler', trigger: 'onSurveyResponseCreated', type: 'firestore_trigger', description: 'Updates address status and workload progress when survey responses are submitted.' },
  { name: 'HubSpot Lead Sync', trigger: 'syncLeadToHubSpot', type: 'firestore_trigger', description: 'Syncs new leads to HubSpot CRM when created.' },
  { name: 'Overdue Task Checker', trigger: 'checkOverdueTasks', type: 'scheduled', description: 'Runs hourly to update overdue task statuses.' },
  { name: 'Tour Reminder Tasks', trigger: 'createTourReminderTasks', type: 'scheduled', description: 'Creates reminder tasks daily at 9 AM for next-day appointments.' },
  { name: 'Leads Not Contacted Alert', trigger: 'checkLeadsNotContacted', type: 'scheduled', description: 'Daily at 10 AM: alerts for leads not contacted within 2 days.' },
  { name: 'Daily Lead Recap', trigger: 'sendDailyLeadRecap', type: 'scheduled', description: 'Sends daily lead recap emails at 9 PM per project timezone.' },
  { name: 'Daily Appointment Reminders', trigger: 'sendDailyAppointmentReminders', type: 'scheduled', description: 'Sends appointment reminder emails at 8 AM per project timezone.' },
  { name: 'Weekly Billing Snapshots', trigger: 'createWeeklyBillingSnapshots', type: 'scheduled', description: 'Creates locked billing snapshots every Monday at midnight PST.' },
  { name: 'Contract Reminders', trigger: 'sendContractReminders', type: 'scheduled', description: 'Sends daily reminders for unsigned contracts.' },
];

const typeConfig = {
  firestore_trigger: { label: 'Firestore Trigger', icon: Database, color: 'bg-blue-100 text-blue-700' },
  scheduled: { label: 'Scheduled', icon: Clock, color: 'bg-amber-100 text-amber-700' },
  callable: { label: 'Callable', icon: Globe, color: 'bg-green-100 text-green-700' },
};

const AutomationsPage: React.FC = () => {
  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <Zap className="h-7 w-7 text-indigo-600" />
        <h1 className="text-2xl font-bold text-gray-900">Automations</h1>
      </div>

      {/* Custom Automation Rules */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Custom Automation Rules</h2>
        <div className="bg-white rounded-lg shadow p-6">
          <AutomationRulesTab />
        </div>
      </div>

      {/* System Rules */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">System Rules</h2>
        <p className="text-sm text-gray-500 mb-4">
          These are built-in automations managed by the platform. They run automatically and cannot be modified.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {systemRules.map((rule) => {
            const config = typeConfig[rule.type];
            const TypeIcon = config.icon;
            return (
              <div key={rule.trigger} className="bg-white rounded-lg shadow p-4 border border-gray-100">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-indigo-500" />
                    <h3 className="text-sm font-semibold text-gray-900">{rule.name}</h3>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mb-3">{rule.description}</p>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                    <TypeIcon className="h-3 w-3" />
                    {config.label}
                  </span>
                  <code className="text-xs text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">{rule.trigger}</code>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AutomationsPage;
